import { Fragment, useState, type ReactNode } from "react";
import { Link, useLocation, useMatch, useNavigate, useResolvedPath } from "react-router";
import { useUpdatesSummary } from "../api/orgUpdates";
import { useMe } from "../api/queries";
import type { MeOrg } from "../api/types";
import { cx } from "../lib/cx";
import { useTheme } from "../lib/theme";
import { useBrand } from "../lib/brand";
import { canManageClients, canSeeDomains, canSeeServers, isAdmin, ROLE_LABEL, seesBilling, clientMay } from "../lib/roles";
import {
  IconActivity,
  IconChevronsUpDown,
  IconMail,
  IconOverview,
  IconSearch,
  IconServers,
  IconSettings,
  IconSidebar,
  IconSites,
  IconCheck,
  IconDomain,
  IconCard,
  IconBriefcase,
  IconPackage,
  IconUsers,
} from "../ui/icons";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from "../ui/Menu";
import { SwitcherSearch, switcherGroups, usePlatformAdmin } from "./accounts";
import { Tooltip } from "../ui/Tooltip";
import { useOrg, useOrgPath } from "./org";
import { useShell } from "./ui";

type NavDef = {
  to: string;
  label: string;
  icon: (p: { size?: number; className?: string }) => ReactNode;
  key: string;
  end?: boolean;
  /** Who sees it; everyone when absent. */
  show?: (org: MeOrg) => boolean;
  /** Items of one group sit together, with a small gap before the next group. */
  group: number;
  /** Pages inside it that light it too (Team's people and groups). */
  also?: string[];
};

// The work, most used first (sites, where they run and keeping them current, then their domains and mail), then
// the people and records around it. Two groups, so hiding Servers never leaves one item on its own.
const NAV: NavDef[] = [
  { to: "", label: "Overview", icon: IconOverview, end: true, key: "o", group: 1 },
  // A client without sites (Mail only) has no Sites or Updates to look at.
  { to: "sites", label: "Sites", icon: IconSites, key: "s", show: (o) => clientMay(o, "sites"), group: 1 },
  // Servers: platform admins, and an account's owners and admins when its plan has server access (docs/plans.md).
  { to: "servers", label: "Servers", icon: IconServers, key: "v", show: canSeeServers, group: 1 },
  { to: "updates", label: "Updates", icon: IconPackage, key: "u", show: (o) => clientMay(o, "sites"), group: 1 },
  { to: "domains", label: "Domains", icon: IconDomain, key: "d", show: (o) => canSeeDomains(o.role, o), group: 1 },
  { to: "mail", label: "Mail", icon: IconMail, key: "m", group: 1 },
  { to: "clients", label: "Clients", icon: IconBriefcase, key: "c", show: (o) => canManageClients(o) && o.kind !== "platform", group: 2 },
  // The platform org's Clients are its accounts: agencies and direct clients, each with its plan (docs/plans.md).
  { to: "accounts", label: "Accounts", icon: IconBriefcase, key: "c", show: (o) => canManageClients(o) && o.kind === "platform", group: 2 },
  // Team is a Settings page; the owners and admins of every org, who invite and manage its people, have it here too.
  { to: "settings/team", label: "Team", icon: IconUsers, key: "t", show: (o) => isAdmin(o.role), group: 2, also: ["settings/people/*", "settings/groups/*"] },
  { to: "activity", label: "Activity", icon: IconActivity, key: "a", group: 2 },
  // The platform org isn't billed; agencies and direct clients are. A shortcut to Settings' Billing, by the same rule.
  { to: "settings/billing", label: "Billing", icon: IconCard, key: "b", show: seesBilling, group: 2 },
];

/** The sections this person sees in this org (Billing for owner and billing, Clients for an agency's admins, and so on). */
export function navFor(org: MeOrg | null): NavDef[] {
  return org ? NAV.filter((n) => !n.show || n.show(org)) : NAV.filter((n) => !n.show);
}

export function useNav(): NavDef[] {
  return navFor(useOrg());
}

const orgKind: Record<MeOrg["kind"], string> = { platform: "Platform", agency: "Agency", client: "Client" };
const roleLabel = ROLE_LABEL;

/** The logo: Hosterr's, or the agency's (its dark logo in the dark theme), or its name when it has no logo. */
export function Wordmark({ collapsed }: { collapsed?: boolean }) {
  const { resolved } = useTheme();
  const brand = useBrand();
  if (!brand.platform) {
    const logo = resolved === "dark" ? (brand.logoDark ?? brand.logoLight) : (brand.logoLight ?? brand.logoDark);
    if (collapsed) {
      return brand.favicon ? (
        <img src={brand.favicon} alt="" width={28} height={28} className="h-7 w-7 rounded-md object-contain" />
      ) : (
        <span aria-hidden="true" className="display inline-flex h-7 w-7 items-center justify-center rounded-md bg-brand-600 text-[14px] text-white">
          {brand.name.trim()[0]?.toUpperCase() ?? "?"}
        </span>
      );
    }
    return logo ? (
      <img src={logo} alt="" className="h-7 max-w-[170px] object-contain object-left" />
    ) : (
      <span className="display block max-w-[170px] truncate text-[19px] leading-none text-fg">{brand.name}</span>
    );
  }
  if (collapsed) return <img src="/brand-mark.png" alt="" width={28} height={28} className="h-7 w-7 dark:brightness-[1.6]" />;
  return <img src={resolved === "dark" ? "/logo_white_accent.webp" : "/logo.png"} alt="" width={109} height={22} className="h-[22px] w-auto" />;
}

/** What's waiting in a section (Updates: how many updates, and whether one is a security fix). */
type NavCount = { n: number; warn: boolean };

/** Whether the address is under any of these (org paths, a trailing /* for the pages inside). */
function useUnder(paths: string[]): boolean {
  const { pathname } = useLocation();
  return paths.some((p) => (p.endsWith("/*") ? pathname.startsWith(p.slice(0, -1)) : pathname === p || pathname.startsWith(`${p}/`)));
}

function NavItem({
  to,
  label,
  icon: Icon,
  end,
  collapsed,
  onNavigate,
  count,
  also = [],
  except = [],
}: {
  to: string;
  label: string;
  icon: (p: { size?: number; className?: string }) => ReactNode;
  end?: boolean;
  collapsed?: boolean;
  onNavigate?: () => void;
  count?: NavCount | null;
  /** Other addresses that light it (the pages inside it). */
  also?: string[];
  /** Addresses under it that light another item instead (Settings' Billing and Team, lit as their shortcuts). */
  except?: string[];
}) {
  // Worked out here rather than with NavLink's className function (and a plain Link, so only the lit item is the page): the tooltip's trigger (a Radix Slot)
  // joins classNames as strings, which turned the function into its source text and gave every item
  // the active look when the sidebar was folded.
  const resolved = useResolvedPath(to);
  const own = useMatch({ path: resolved.pathname, end: !!end }) != null;
  const inside = useUnder(also);
  const elsewhere = useUnder(except);
  const isActive = (own || inside) && !elsewhere;
  const waiting = count ? `${count.n} waiting${count.warn ? ", with security fixes" : ""}` : null;
  const link = (
    <Link
      to={to}
      aria-current={isActive ? "page" : undefined}
      onClick={onNavigate}
      aria-label={collapsed ? (waiting ? `${label}, ${waiting}` : label) : undefined}
      className={cx(
        "group relative flex items-center rounded-[9px] text-[14px] font-medium transition-[background-color,color] duration-150 focus-visible:outline-offset-1",
        collapsed ? "h-10 w-10 justify-center" : "h-9.5 gap-3 px-2.5",
        // Where you are: the brand's pale tint, with brand text and icon. Hover stays gray.
        isActive ? "bg-selected text-selected-fg" : "text-body hover:bg-hover hover:text-fg",
      )}
    >
      <Icon size={18} className={cx("shrink-0 transition-colors", isActive ? "text-current" : "text-dim group-hover:text-fg")} />
      {collapsed ? null : <span className="truncate">{label}</span>}
      {count && collapsed ? (
        // Folded: a small dot on the icon, amber when a security fix waits.
        <span aria-hidden="true" className={cx("absolute top-2 right-2 h-[7px] w-[7px] rounded-full", count.warn ? "bg-warn-fill" : "bg-dim")} />
      ) : count ? (
        <>
          {/* Quiet, like a rail's counts; a security fix makes it a small amber chip, which reads on the brand tint too. */}
          <span
            aria-hidden="true"
            className={cx(
              "ml-auto shrink-0 tabular-nums",
              count.warn
                ? "rounded-full bg-warn-bg px-1.5 text-[11.5px] leading-[18px] font-semibold text-warn shadow-[inset_0_0_0_1px_var(--hx-warn-line)]"
                : cx("text-[12px] leading-5", isActive ? "text-selected-fg" : "text-dim"),
            )}
          >
            {count.n}
          </span>
          <span className="sr-only">, {waiting}</span>
        </>
      ) : null}
    </Link>
  );
  return collapsed ? (
    <Tooltip content={waiting ? `${label} · ${waiting}` : label} side="right">
      {link}
    </Tooltip>
  ) : (
    link
  );
}

function OrgBadge({ org, size = 32, flat }: { org: MeOrg; size?: number; flat?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.47) }}
      className={cx("display inline-flex shrink-0 items-center justify-center rounded-[9px] bg-brand-600 text-white", !flat && "shadow-[var(--hx-shadow-brand)]")}
    >
      {org.name.trim()[0]?.toUpperCase() ?? "?"}
    </span>
  );
}

function OrgSwitcher({ collapsed }: { collapsed?: boolean }) {
  const org = useOrg();
  const me = useMe();
  const navigate = useNavigate();
  const orgs = me.data?.orgs ?? [org];
  const meta = `${orgKind[org.kind]} · ${roleLabel[org.role]}`;
  // A platform admin's accounts are their own group, with a search once there are many (app/accounts.tsx).
  const groups = switcherGroups(orgs, usePlatformAdmin());
  const [find, setFind] = useState("");
  const many = orgs.length > 8;
  const match = (o: MeOrg) => !find.trim() || o.name.toLowerCase().includes(find.trim().toLowerCase());
  const own = groups.own.filter(match);
  const accounts = groups.accounts.filter((a) => match(a.org));
  const item = (o: MeOrg, indent = false) => (
    <MenuItem key={o.id} onSelect={() => navigate(`/o/${o.slug}`)} icon={<OrgBadge org={o} size={24} />} className={indent ? "pl-7" : undefined}>
      <span className="flex items-center justify-between gap-2">
        <span className="min-w-0">
          <span className="block truncate text-fg">{o.name}</span>
          <span className="block text-[12px] text-dim">{orgKind[o.kind]}</span>
        </span>
        {o.id === org.id ? <IconCheck size={15} className="shrink-0 text-accent" /> : null}
      </span>
    </MenuItem>
  );

  const body = (
    <>
      {collapsed ? <OrgBadge org={org} size={28} flat /> : <OrgBadge org={org} />}
      {collapsed ? null : (
        <span className="min-w-0 flex-1 text-left">
          <span className="block truncate text-[14px] font-semibold text-fg">{org.name}</span>
          <span className="block truncate text-[12px] text-dim">{meta}</span>
        </span>
      )}
    </>
  );

  // Only one org: nothing to switch to, so this is a label, not a control.
  if (orgs.length < 2) {
    return (
      <div className={cx("flex items-center gap-2.5 rounded-[10px]", collapsed ? "h-10 w-10 justify-center" : "px-1.5 py-1")} title={collapsed ? org.name : undefined}>
        {body}
      </div>
    );
  }

  return (
    <Menu onOpenChange={(open) => !open && setFind("")}>
      <MenuTrigger
        aria-label={`Organization: ${org.name}. Switch organization`}
        className={cx(
          "flex items-center gap-2.5 rounded-[10px] transition-colors hover:bg-hover focus-visible:outline-2 focus-visible:outline-brand-500 data-[state=open]:bg-hover",
          collapsed ? "h-10 w-10 justify-center" : "w-full px-1.5 py-1",
        )}
      >
        {body}
        {collapsed ? null : <IconChevronsUpDown size={16} className="shrink-0 text-dim" />}
      </MenuTrigger>
      <MenuContent align="start" className="w-[280px]">
        {many ? <SwitcherSearch value={find} onChange={setFind} /> : null}
        {own.length ? <MenuLabel>Organizations</MenuLabel> : null}
        {own.map((o) => item(o))}
        {accounts.length ? (
          <>
            {own.length ? <MenuSeparator /> : null}
            <MenuLabel>Accounts</MenuLabel>
            {accounts.map((a) => item(a.org, a.indent))}
          </>
        ) : null}
        {!own.length && !accounts.length ? <p className="px-2.5 py-2 text-[13.5px] text-dim">Nothing matches “{find.trim()}”.</p> : null}
      </MenuContent>
    </Menu>
  );
}

/** Folds the sidebar to icons and back. It sits in the same spot, under Settings, whether the sidebar is open or folded. */
function SidebarToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const label = collapsed ? "Expand sidebar" : "Collapse sidebar";
  return (
    <Tooltip content={`${label}  [`} side="right">
      <button
        type="button"
        onClick={onToggle}
        aria-label={collapsed ? label : undefined}
        aria-expanded={!collapsed}
        className={cx(
          "flex items-center rounded-[9px] text-[13.5px] text-dim transition-colors hover:bg-hover hover:text-fg focus-visible:outline-2 focus-visible:outline-brand-500",
          collapsed ? "h-10 w-10 justify-center" : "h-9.5 w-full gap-3 px-2.5",
        )}
      >
        <IconSidebar size={18} className={cx("shrink-0", collapsed && "-scale-x-100")} />
        {collapsed ? null : <span className="truncate">{label}</span>}
      </button>
    </Tooltip>
  );
}

export function SidebarContent({
  collapsed,
  onToggle,
  onNavigate,
}: {
  collapsed?: boolean;
  /** Absent in the mobile drawer, which has its own close button. */
  onToggle?: () => void;
  onNavigate?: () => void;
}) {
  const orgPath = useOrgPath();
  const shell = useShell();
  const brand = useBrand();
  const nav = useNav();
  // Updates waiting, counted by update (not by where they wait); nothing while it loads or at none.
  const updates = useUpdatesSummary(nav.some((n) => n.to === "updates"));
  const waiting: NavCount | null = updates.data?.updates ? { n: updates.data.updates, warn: updates.data.security } : null;

  return (
    <div className="flex h-full flex-col">
      {/* The logo's band: the top bar's height plus its 1px bottom line (57px), so the two lines meet as one. */}
      <div className={cx("flex h-[57px] shrink-0 items-center border-b border-line", collapsed ? "justify-center px-2" : "pl-5")}>
        <Link
          to={orgPath()}
          onClick={onNavigate}
          aria-label={`${brand.name} overview`}
          className="rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-500"
        >
          <Wordmark collapsed={collapsed} />
        </Link>
      </div>

      <div className={cx("pt-3", collapsed ? "flex flex-col items-center gap-1 px-2" : "space-y-3 px-3")}>
        <OrgSwitcher collapsed={collapsed} />
        {collapsed ? (
          <Tooltip content="Search  ⌘K" side="right">
            <button
              type="button"
              onClick={shell.openPalette}
              aria-label="Search and commands"
              className="inline-flex h-10 w-10 items-center justify-center rounded-[9px] text-dim transition-colors hover:bg-hover hover:text-fg focus-visible:outline-2 focus-visible:outline-brand-500"
            >
              <IconSearch size={18} />
            </button>
          </Tooltip>
        ) : (
          <button
            type="button"
            onClick={() => {
              onNavigate?.();
              shell.openPalette();
            }}
            className="flex h-9.5 w-full items-center gap-2.5 rounded-[9px] bg-surface px-2.5 text-[13.5px] text-dim shadow-[inset_0_0_0_1px_var(--hx-line)] transition-shadow hover:shadow-[inset_0_0_0_1px_var(--hx-line-strong)] focus-visible:outline-2 focus-visible:outline-brand-500"
          >
            <IconSearch size={16} />
            <span className="flex-1 text-left">Search</span>
            {/* The shortcut, where there's a keyboard: not on a phone or tablet (the drawer). */}
            <kbd className="rounded bg-panel px-1.5 py-0.5 font-mono text-[10.5px] text-dim shadow-[inset_0_0_0_1px_var(--hx-line)] pointer-coarse:hidden">⌘K</kbd>
          </button>
        )}
      </div>

      <nav aria-label="Main" className={cx("mt-4 flex-1 overflow-y-auto pt-1 pb-1", collapsed ? "flex flex-col items-center gap-1 px-2" : "space-y-1 px-3")}>
        {nav.map((n, i) => (
          <Fragment key={n.label}>
            {i > 0 && nav[i - 1]?.group !== n.group ?<div aria-hidden="true" className="h-2" /> : null}
            <NavItem to={orgPath(n.to)} label={n.label} icon={n.icon} end={!!n.end} collapsed={collapsed} onNavigate={onNavigate} count={n.to === "updates" ? waiting : null} also={n.also?.map((p) => orgPath(p))} />
          </Fragment>
        ))}
      </nav>

      <div className={cx("shrink-0 border-t border-line pt-3 pb-3", collapsed ? "flex flex-col items-center gap-1 px-2" : "space-y-1 px-3")}>
        {/* Settings is lit on its pages, except one the sidebar has as its own item (Billing, Team): that one is lit instead. */}
        <NavItem
          to={orgPath("settings")}
          label="Settings"
          icon={IconSettings}
          collapsed={collapsed}
          onNavigate={onNavigate}
          except={nav.filter((n) => n.to.startsWith("settings/")).flatMap((n) => [orgPath(n.to), ...(n.also ?? []).map((p) => orgPath(p))])}
        />
        {onToggle ? <SidebarToggle collapsed={!!collapsed} onToggle={onToggle} /> : null}
      </div>
    </div>
  );
}
